import * as Sentry from "@sentry/react";

/**
 * Utility to extract consent data from Ketch.
 *
 * Ketch can return purposes in two formats:
 * 1. Boolean: { targeted_advertising: true }
 * 2. Object: { targeted_advertising: { allowed: "true", legalBasisCode: "..." } }
 *
 * We normalise both to booleans for backend storage.
 */

// Ketch can return either a boolean or this object structure
interface PurposeAllowed {
  allowed: string;
  legalBasisCode?: string;
}

type PurposeValue = boolean | PurposeAllowed | undefined;

interface KetchConsentPurposes {
  targeted_advertising?: PurposeValue;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Ketch API uses this key
  analytics_?: PurposeValue;
  essential_services?: PurposeValue;
}

interface ConsentPayload {
  targetedAdvertising?: boolean;
  analytics?: boolean;
  essentialServices?: boolean;
}

export interface KetchConsent {
  purposes?: KetchConsentPurposes;
}

type KetchConsentHandler = (consent: KetchConsent) => void;

declare global {
  interface Window {
    ketch?: {
      (command: string, callback: KetchConsentHandler): void;
      (command: "on" | "off", event: "consent", handler: KetchConsentHandler): void;
    };
    // Ketch purpose code -> granted, read by the RudderStack SDK's Ketch consent plugin
    ketchConsent?: Record<string, boolean>;
    // Queue of Ketch commands issued before the boot script has loaded
    semaphore?: unknown[];
  }
}

/**
 * Converts a Ketch purpose value to a boolean.
 *
 * Ketch returns purposes in two possible formats:
 * - Boolean: true or false
 * - PurposeAllowed object: { allowed: "true" | "false", legalBasisCode?: string }
 *
 * This function normalizes both formats to a boolean for the backend API.
 */
function convertKetchPurposeToBoolean(value: PurposeValue): boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "object" && "allowed" in value) {
    return value.allowed === "true";
  }

  return undefined;
}

/**
 * How long to wait for Ketch to invoke its `getConsent` callback before giving up.
 *
 * `window.ketch` may be a queueing stub whose callbacks are only ever invoked once the Ketch
 * SDK has loaded and drained the queue. When the SDK does not load, the callback never runs,
 * so an unbounded wait here blocks every caller indefinitely. Consent is best-effort
 * metadata and must never gate the operation it is attached to.
 */
const GET_CONSENT_TIMEOUT_MS = 2000;

/**
 * Gets the current Ketch consent purposes.
 * Returns a promise that resolves to the consent JSON string, or null if Ketch is not
 * available, does not respond within {@link GET_CONSENT_TIMEOUT_MS}, or no consent is set.
 */
export async function getKetchConsent(): Promise<string | null> {
  return new Promise((resolve) => {
    // Check if Ketch is available
    if (typeof window.ketch !== "function") {
      console.warn("Ketch not available");
      resolve(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      const message = `Ketch did not respond within ${GET_CONSENT_TIMEOUT_MS}ms`;
      console.warn(message);
      Sentry.captureMessage(message);
      resolve(null);
    }, GET_CONSENT_TIMEOUT_MS);

    try {
      window.ketch("getConsent", (consent: KetchConsent) => {
        clearTimeout(timeoutId);

        if (!consent?.purposes) {
          resolve(null);
          return;
        }

        // Extract purposes, handling both boolean and object formats
        // Convert to camelCase for API payload
        const purposes: ConsentPayload = {
          targetedAdvertising: convertKetchPurposeToBoolean(consent.purposes?.targeted_advertising),
          // eslint-disable-next-line no-underscore-dangle -- Ketch SDK uses 'analytics_' as the key
          analytics: convertKetchPurposeToBoolean(consent.purposes?.analytics_),
          essentialServices: convertKetchPurposeToBoolean(consent.purposes?.essential_services),
        };

        resolve(JSON.stringify(purposes));
      });
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Error getting Ketch consent:", error);
      Sentry.captureException(error);
      resolve(null);
    }
  });
}

/**
 * Maps every Ketch purpose to whether it is granted, keyed by the Ketch purpose code
 * (e.g. { targeted_advertising: false, analytics_: true, essential_services: true }).
* This is the shape the RudderStack SDK's Ketch consent plugin consumes. A missing consent
 * object yields an empty map, which downstream reads as nothing granted.
 */
export function ketchPurposesGranted(
  consent: KetchConsent | null | undefined,
): Record<string, boolean> {
  return Object.fromEntries(
    Object.entries(consent?.purposes ?? {}).map(([purposeCode, value]) => [
      purposeCode,
      convertKetchPurposeToBoolean(value as PurposeValue) === true,
    ]),
  );
}
